import { TFunction } from "i18next";
import { ExpandedColumn } from "library/types/columns";
import { ProcessedTaxonomyState } from "library/types/types";
import { format } from "date-fns";
import { formatDateWithTime } from "library/utils";

export type ExportTarget = "excel" | "pdf";

/** Period (offer request date) an export was limited to */
export type ExportPeriod = { from: Date; to: Date };

export type ExportCellValue = string | number | null;

/** Column keys whose value is a plain yes/no flag. */
const YES_NO_KEYS = new Set([
  "ClientIsResident",
  "InvoiceMtplPayerIsResident",
  "InvoiceCascoPayerIsResident",
  "GroupInvoicePayerIsResident",
  "VehicleIsConvertible",
  "VehicleIsKeyless",
  "InvoiceMtplIsPaid",
  "InvoiceCascoIsPaid",
]);

/** Column keys whose value is a company/person flag. */
const COMPANY_PERSON_KEYS = new Set([
  "ClientIsCompany",
  "InvoiceMtplPayerIsCompany",
  "InvoiceCascoPayerIsCompany",
  "GroupInvoicePayerIsCompany",
]);

/**
 * Column keys backed by a taxonomy group. Value in the row is the taxonomy id
 * (or code, for the LV-specific groups). `translate` mirrors the grid cells:
 * some groups have translations for their codes, others display the raw code.
 */
const TAXONOMY_KEYS: Record<
  string,
  { group: string; translate: boolean }
> = {
  OfferStatus: { group: "OfferStatus", translate: true },
  VehicleTypeID: { group: "VehicleType", translate: true },
  VehicleTransportTypeCode: { group: "VehicleTransportType", translate: false },
  InsuranceObjectUsage: { group: "VehicleUsageTypeLV", translate: false },
  VehiclePowerTypeCode: { group: "VehiclePowerType", translate: true },
};

/** Columns that are only meaningful on screen (context menu, selection, ...). */
const NON_EXPORTABLE_KEYS = new Set(["options"]);

export const getExportableColumns = (columns: ExpandedColumn[]) =>
  columns.filter(
    (c) =>
      c.isVisible &&
      !NON_EXPORTABLE_KEYS.has(c.key) &&
      !(c.excel && c.excel.isVisible === false)
  );

export const isNumberColumn = (column: ExpandedColumn) =>
  column.filter?.type === "number";

export const isCurrencyColumn = (column: ExpandedColumn) =>
  column.filter?.type === "number" && column.filter.format === "float";

const resolveTaxonomyLabel = (
  key: string,
  value: any,
  taxonomy: ProcessedTaxonomyState | null,
  t: TFunction
): ExportCellValue => {
  if (value === null || value === undefined || value === "") return null;
  const cfg = TAXONOMY_KEYS[key];
  const item = taxonomy?.[cfg.group]?.byId?.[value];
  if (!item) return String(value);
  return cfg.translate ? t(item.code, item.code) : item.code;
};

const formatOfferedInsurers = (row: any): ExportCellValue => {
  const mtpl: string[] = row?.OfferedInsurersMtpl || [];
  const casco: string[] = row?.OfferedInsurersCasco || [];
  const parts: string[] = [];
  if (mtpl.length > 0) parts.push(`MTPL: ${mtpl.join(", ")}`);
  if (casco.length > 0) parts.push(`CASCO: ${casco.join(", ")}`);
  return parts.length > 0 ? parts.join("; ") : null;
};

/**
 * Produces the value for one cell of an exported document. Mirrors what the
 * grid displays: taxonomy codes are resolved, booleans become Yes/No, dates
 * and numbers use the column's own `transform`. For Excel, numeric columns keep
 * the raw number so the sheet stays sortable and summable.
 */
export const getOfferExportValue = ({
  column,
  row,
  taxonomy,
  t,
  target,
}: {
  column: ExpandedColumn;
  row: any;
  taxonomy: ProcessedTaxonomyState | null;
  t: TFunction;
  target: ExportTarget;
}): ExportCellValue => {
  const key = column.key;

  if (column.excel?.render && target === "excel") {
    return column.excel.render({ t, taxonomy: taxonomy as any, row });
  }

  const raw = row?.[key];

  if (key === "OfferedInsurers") return formatOfferedInsurers(row);

  if (YES_NO_KEYS.has(key)) {
    if (raw === null || raw === undefined) return null;
    return raw ? t("greco.yes") : t("greco.no");
  }

  if (COMPANY_PERSON_KEYS.has(key)) {
    if (raw === null || raw === undefined) return null;
    return raw
      ? t("qtg.clientType.company.label")
      : t("qtg.clientType.person.label");
  }

  if (TAXONOMY_KEYS[key]) {
    return resolveTaxonomyLabel(key, raw, taxonomy, t);
  }

  if (target === "excel" && isNumberColumn(column)) {
    if (raw === null || raw === undefined || raw === "") return null;
    const n = typeof raw === "number" ? raw : Number(raw);
    return isNaN(n) ? String(raw) : n;
  }

  // Fall back to the grid's own transform (dates, formatted numbers, ...)
  try {
    const detailsColumn = column.detailsListColumn({
      t,
      taxonomy: (taxonomy || {}) as any,
      width: column.width,
    });
    if (detailsColumn?.transform) {
      const v = detailsColumn.transform({ row, taxonomy: taxonomy as any, t });
      if (v === null || v === undefined) return null;
      return typeof v === "number" ? v : String(v);
    }
  } catch (err) {
    console.warn("Error transforming column for export:", key, err);
  }

  if (raw === null || raw === undefined) return null;
  if (Array.isArray(raw)) return raw.join(", ");
  if (typeof raw === "object") return JSON.stringify(raw);
  if (typeof raw === "boolean") return raw ? t("greco.yes") : t("greco.no");
  return raw;
};

/** Flattens offer rows into `{ [columnKey]: cellValue }` objects for export. */
export const transformOffersForExport = ({
  rows,
  columns,
  taxonomy,
  t,
  target,
}: {
  rows: any[];
  columns: ExpandedColumn[];
  taxonomy: ProcessedTaxonomyState | null;
  t: TFunction;
  target: ExportTarget;
}): Record<string, ExportCellValue>[] =>
  rows.map((row) =>
    columns.reduce((acc, column) => {
      acc[column.key] = getOfferExportValue({
        column,
        row,
        taxonomy,
        t,
        target,
      });
      return acc;
    }, {} as Record<string, ExportCellValue>)
  );

/** Offers_<from>_<to>_<export timestamp>.<ext>, e.g. Offers_2026-07-29_2026-09-29_2026_09_29_16_10_00.xlsx */
export const getExportFileName = (
  extension: "xlsx" | "pdf",
  period?: ExportPeriod,
) => {
  const periodPart = period
    ? `${format(period.from, "yyyy-MM-dd")}_${format(period.to, "yyyy-MM-dd")}_`
    : "";
  return `Offers_${periodPart}${formatDateWithTime(new Date())}.${extension}`;
};
